import { partition, uniqueId } from 'lodash';
import { useCallback } from 'react';
import { type FileWithPath } from 'react-dropzone';
import { useDispatch } from 'react-redux';

import { exceedsBrowserImagePreviewLimit } from '@air/feature-uploads/utils';
import { queueUploadsAction, type Upload } from '@air/redux-uploader';
import { createUpload } from '@air/upload-utils';

import { handleRejectedFiles } from '~/components/PrivateDropzone/utilities/dropzones';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { setPrivateUploadsMetadata } from '~/store/privateUploadMetadata/actions';
import { getFilePath } from '~/store/uploadingPathToBoardId/utils';
import { type BoardsSelectRootOption } from '~/types/BoardSearch';
import { sanitizeRestrictedPathChars } from '~/utils/FileUtils';
import { registerUploadDimensions } from '~/utils/uploadDimensionsCache';

export type TopLevelFileUploadDecision = {
  file: FileWithPath;
  assetId?: string;
};

type UploadAndMetadata = {
  upload: Upload;
  metadata: {
    assetId?: string;
    isVersion: boolean;
    originBoard?: BoardsSelectRootOption;
    filepath: string;
    workspaceId: string;
  };
};

export const splitFilesByFolder = (files: FileWithPath[]) => partition(files, (file) => file.path?.includes('/'));

const THUMBNAIL_SIZE = 400;

const getImageDimensions = async (
  file: FileWithPath,
): Promise<{ width: number; height: number; imageBase64: string } | undefined> => {
  if (!file.type.startsWith('image/') || exceedsBrowserImagePreviewLimit(file.size)) {
    return undefined;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;

    let imageBase64: string;
    try {
      const scale = Math.min(1, THUMBNAIL_SIZE / width, THUMBNAIL_SIZE / height);
      const canvas = new OffscreenCanvas(Math.round(width * scale), Math.round(height * scale));
      canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.85 });
      imageBase64 = URL.createObjectURL(blob);
    } catch {
      // OffscreenCanvas not supported — fall back to full-res blob URL
      imageBase64 = URL.createObjectURL(file);
    }

    bitmap.close();
    return { width, height, imageBase64 };
  } catch {
    return undefined;
  }
};

const DIMENSION_BATCH_SIZE = 20;

type QueuePrivateUploadsParams = {
  filesInFolders: FileWithPath[];
  topLevelFilesToUpload: TopLevelFileUploadDecision[];
  boardToUpload?: BoardsSelectRootOption;
  rejectedFiles: FileWithPath[];
  emptyFiles: FileWithPath[];
  filesWithNoExtension: FileWithPath[];
};

export const useQueuePrivateUploads = () => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useCurrentWorkspace();

  const queuePrivateUploads = useCallback(
    async ({
      filesInFolders,
      topLevelFilesToUpload,
      boardToUpload,
      rejectedFiles,
      emptyFiles,
      filesWithNoExtension,
    }: QueuePrivateUploadsParams) => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        return;
      }

      const batchId = uniqueId('batch');

      const dispatchFolderBatch = async (batch: FileWithPath[]) => {
        const dimensions = await Promise.all(batch.map(getImageDimensions));
        const uploads = batch.map((file, i) => ({ ...createUpload({ file, batchId }), ...dimensions[i] }));
        const metadata = uploads.map((upload) => ({
          uploadId: upload.id,
          metadata: {
            isVersion: false,
            originBoard: boardToUpload,
            filepath: sanitizeRestrictedPathChars(upload.file.path ? getFilePath(upload.file.path) : ''),
            workspaceId,
          },
        }));
        for (const upload of uploads) {
          if (upload.width && upload.height) {
            registerUploadDimensions(upload.apiUploadData.importedName, { width: upload.width, height: upload.height });
          }
        }
        dispatch(queueUploadsAction({ uploads }));
        dispatch(setPrivateUploadsMetadata({ uploadsMetadata: metadata }));
      };

      const dispatchTopLevelBatch = async (batch: TopLevelFileUploadDecision[]) => {
        const dimensions = await Promise.all(batch.map(({ file }) => getImageDimensions(file)));
        const uploadsAndMetadata = batch.map<UploadAndMetadata>(({ file, assetId }, i) => ({
          upload: { ...createUpload({ file, batchId }), ...dimensions[i] },
          metadata: {
            assetId,
            isVersion: Boolean(assetId),
            originBoard: boardToUpload,
            filepath: file.path ? getFilePath(file.path) : '',
            workspaceId,
          },
        }));
        const uploads = uploadsAndMetadata.map(({ upload }) => upload);
        const metadata = uploadsAndMetadata.map(({ upload, metadata }) => ({ uploadId: upload.id, metadata }));
        for (const upload of uploads) {
          if (upload.width && upload.height) {
            registerUploadDimensions(upload.apiUploadData.importedName, { width: upload.width, height: upload.height });
          }
        }
        dispatch(queueUploadsAction({ uploads }));
        dispatch(setPrivateUploadsMetadata({ uploadsMetadata: metadata }));
      };

      const processFolders = async () => {
        for (let i = 0; i < filesInFolders.length; i += DIMENSION_BATCH_SIZE) {
          await dispatchFolderBatch(filesInFolders.slice(i, i + DIMENSION_BATCH_SIZE));
        }
      };

      const processTopLevel = async () => {
        for (let i = 0; i < topLevelFilesToUpload.length; i += DIMENSION_BATCH_SIZE) {
          await dispatchTopLevelBatch(topLevelFilesToUpload.slice(i, i + DIMENSION_BATCH_SIZE));
        }
      };

      await Promise.all([processFolders(), processTopLevel()]);

      if (rejectedFiles.length + emptyFiles.length + filesWithNoExtension.length > 0) {
        dispatch(handleRejectedFiles({ rejectedFiles, emptyFiles, filesWithNoExtension }));
      }
    },
    [currentWorkspace?.id, dispatch],
  );

  return { queuePrivateUploads };
};
